'use client';

import {useFeatures} from '@udemy-v2/react-experimentation';

import {useInstructorCommunityBannerInfoByInstructorIdQuery} from '@/generated/graphql';

/**
 * Feature code gating the instructor community badges/ribbon/banner.
 * Monolith: ExpPlatform.get_feature_variant("instructor_community")
 */
export const FEATURE_INSTRUCTOR_COMMUNITY = 'instructor_community';

/**
 * Fetches an instructor's published community banner info via GraphQL
 * (instructorCommunityBannerInfoByInstructorId), gated behind the
 * instructor_community experiment. Returns null/false when the experiment
 * is disabled for the current viewer, or when the instructor has no
 * published community.
 */
export function useInstructorCommunityBannerInfo(
    instructorId: string | number | null | undefined,
) {
    const {featureVariantAssignments} = useFeatures([FEATURE_INSTRUCTOR_COMMUNITY]);
    const isExperimentEnabled =
        featureVariantAssignments.get(FEATURE_INSTRUCTOR_COMMUNITY)?.configuration?.enabled ===
        true;

    const query = useInstructorCommunityBannerInfoByInstructorIdQuery(
        {instructorId: String(instructorId)},
        {enabled: instructorId != null && isExperimentEnabled},
    );

    const bannerInfo = isExperimentEnabled
        ? (query.data?.instructorCommunityBannerInfoByInstructorId ?? null)
        : null;

    return {
        bannerInfo,
        hasCommunity: isExperimentEnabled && bannerInfo != null,
        isLoading: isExperimentEnabled && query.isLoading,
        error: (query.error ?? undefined) as Error | undefined,
    };
}
